/**
 * Inventory Management — physical stock across hostel rooms + custom locations.
 * Reuses existing Buildings/Floors/Rooms. Does NOT duplicate the room database.
 *
 * Tabs: Dashboard · Overview · Item-wise · Location-wise · Room Matrix ·
 *       Store · Movements · Items · Locations · Import · Reports
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Boxes, Plus, Search, Download, Upload, ArrowRightLeft, SlidersHorizontal,
  Warehouse, MapPin, Package, LayoutGrid, History as HistoryIcon,
  Pencil, Trash2, AlertTriangle, Wrench, XCircle,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { PaginationBar } from "@/components/PaginationBar";

/* ---------------- Constants ---------------- */
const CATEGORIES = [
  { v: "furniture",  l: "Furniture" },
  { v: "bedding",    l: "Bedding" },
  { v: "electrical", l: "Electrical" },
  { v: "bathroom",   l: "Bathroom" },
  { v: "cleaning",   l: "Cleaning" },
  { v: "kitchen",    l: "Kitchen" },
  { v: "office",     l: "Office" },
  { v: "equipment",  l: "Equipment" },
  { v: "other",      l: "Other" },
];
const UNITS = [
  { v: "piece", l: "Piece" },  { v: "set", l: "Set" },
  { v: "pair", l: "Pair" },    { v: "box", l: "Box" },
  { v: "bundle", l: "Bundle" }, { v: "bottle", l: "Bottle" },
  { v: "other", l: "Other" },
];
const LOC_TYPES = [
  { v: "hostel_room",  l: "Hostel Room" },
  { v: "store",        l: "Store" },
  { v: "office",       l: "Office" },
  { v: "reception",    l: "Reception" },
  { v: "kitchen",      l: "Kitchen" },
  { v: "dining_hall",  l: "Dining Hall" },
  { v: "common_area",  l: "Common Area" },
  { v: "maintenance",  l: "Maintenance" },
  { v: "other",        l: "Other" },
];
const CONDITIONS = [
  { v: "new", l: "New", tone: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  { v: "good", l: "Good", tone: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  { v: "used", l: "Used", tone: "bg-amber-50 text-amber-700 border-amber-200" },
  { v: "damaged", l: "Damaged", tone: "bg-red-50 text-red-700 border-red-200" },
  { v: "under_repair", l: "Under Repair", tone: "bg-orange-50 text-orange-700 border-orange-200" },
  { v: "missing", l: "Missing", tone: "bg-rose-50 text-rose-700 border-rose-200" },
  { v: "disposed", l: "Disposed", tone: "bg-slate-100 text-slate-600 border-slate-200" },
];
const ADJUST_REASONS = [
  "damaged", "missing", "repaired", "disposed", "found",
  "audit_correction", "physical_count", "wear_and_tear", "other",
];

const condLabel = (v) => CONDITIONS.find((c) => c.v === v)?.l || v;
const condTone  = (v) => CONDITIONS.find((c) => c.v === v)?.tone || "bg-gray-50 text-gray-600 border-gray-200";
const catLabel  = (v) => CATEGORIES.find((c) => c.v === v)?.l || v;
const locTypeLabel = (v) => LOC_TYPES.find((l) => l.v === v)?.l || v;

/* ---------------- Main ---------------- */
export default function Inventory() {
  const { user } = useAuth();
  const role = user?.role;
  const canEdit = ["admin", "hostel_manager"].includes(role);
  const canTransfer = ["admin", "hostel_manager", "warden"].includes(role);
  const canDelete = role === "admin";

  const [tab, setTab] = useState("dashboard");
  const [items, setItems] = useState([]);
  const [locations, setLocations] = useState([]);
  const [dashboard, setDashboard] = useState(null);

  const loadItems = useCallback(() => api.get("/inventory/items?active_only=false")
    .then((r) => setItems(r.data)).catch(() => {}), []);
  const loadLocations = useCallback(() => api.get("/inventory/locations?active_only=false")
    .then((r) => setLocations(r.data)).catch(() => {}), []);
  const loadDashboard = useCallback(() => api.get("/inventory/dashboard")
    .then((r) => setDashboard(r.data)).catch(() => {}), []);
  const reloadAll = useCallback(() => {
    loadItems(); loadLocations(); loadDashboard();
  }, [loadItems, loadLocations, loadDashboard]);

  useEffect(() => { reloadAll(); }, [reloadAll]);

  const itemMap = useMemo(() => Object.fromEntries(items.map((i) => [i.id, i])), [items]);
  const locMap  = useMemo(() => Object.fromEntries(locations.map((l) => [l.id, l])), [locations]);

  return (
    <div className="space-y-6" data-testid="inventory-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="eyebrow text-muted-foreground">Warehouse</div>
          <h1 className="font-display text-4xl font-bold mt-1 flex items-center gap-2">
            <Boxes className="w-8 h-8 text-primary" /> Inventory
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Track physical stock across hostel rooms, store and custom locations.
          </p>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="flex flex-wrap h-auto" data-testid="inventory-tabs">
          <TabsTrigger value="dashboard" data-testid="tab-dashboard">Dashboard</TabsTrigger>
          <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="by-item" data-testid="tab-by-item">Item-wise</TabsTrigger>
          <TabsTrigger value="by-location" data-testid="tab-by-location">Location-wise</TabsTrigger>
          <TabsTrigger value="matrix" data-testid="tab-matrix">Room Matrix</TabsTrigger>
          <TabsTrigger value="store" data-testid="tab-store">Store</TabsTrigger>
          <TabsTrigger value="movements" data-testid="tab-movements">Movements</TabsTrigger>
          <TabsTrigger value="items" data-testid="tab-items">Items</TabsTrigger>
          <TabsTrigger value="locations" data-testid="tab-locations">Locations</TabsTrigger>
          {canEdit && <TabsTrigger value="import" data-testid="tab-import">Import</TabsTrigger>}
          <TabsTrigger value="reports" data-testid="tab-reports">Reports</TabsTrigger>
        </TabsList>

        <TabsContent value="dashboard" className="mt-4">
          <DashboardPanel dashboard={dashboard} onDrill={(mode) => { setTab(mode); }} />
        </TabsContent>
        <TabsContent value="overview" className="mt-4">
          <OverviewPanel items={items} locations={locations} canEdit={canEdit}
            canTransfer={canTransfer} onChanged={reloadAll} itemMap={itemMap} locMap={locMap} />
        </TabsContent>
        <TabsContent value="by-item" className="mt-4">
          <ByItemPanel items={items} locMap={locMap} />
        </TabsContent>
        <TabsContent value="by-location" className="mt-4">
          <ByLocationPanel locations={locations} itemMap={itemMap} />
        </TabsContent>
        <TabsContent value="matrix" className="mt-4">
          <MatrixPanel />
        </TabsContent>
        <TabsContent value="store" className="mt-4">
          <StorePanel locations={locations} itemMap={itemMap} />
        </TabsContent>
        <TabsContent value="movements" className="mt-4">
          <MovementsPanel />
        </TabsContent>
        <TabsContent value="items" className="mt-4">
          <ItemsPanel items={items} canEdit={canEdit} canDelete={canDelete}
            onChanged={loadItems} />
        </TabsContent>
        <TabsContent value="locations" className="mt-4">
          <LocationsPanel locations={locations} canEdit={canEdit} canDelete={canDelete}
            onChanged={loadLocations} />
        </TabsContent>
        {canEdit && (
          <TabsContent value="import" className="mt-4">
            <ImportPanel onDone={reloadAll} />
          </TabsContent>
        )}
        <TabsContent value="reports" className="mt-4">
          <ReportsPanel />
        </TabsContent>
      </Tabs>
    </div>
  );
}

/* ==================== DASHBOARD PANEL ==================== */
function DashboardPanel({ dashboard, onDrill }) {
  if (!dashboard) return <Card className="p-8 text-muted-foreground">Loading…</Card>;
  const cards = [
    { key: "total_items", label: "Total Items", value: dashboard.total_items, icon: Package, tone: "bg-primary/10 text-primary", to: "items" },
    { key: "total_quantity", label: "Total Quantity", value: dashboard.total_quantity, icon: Boxes, tone: "bg-primary/10 text-primary", to: "overview" },
    { key: "in_rooms", label: "In Hostel Rooms", value: dashboard.in_rooms, icon: LayoutGrid, tone: "bg-emerald-50 text-emerald-700", to: "matrix" },
    { key: "in_store", label: "In Store", value: dashboard.in_store, icon: Warehouse, tone: "bg-amber-50 text-amber-700", to: "store" },
    { key: "available", label: "Available (Good)", value: dashboard.available, icon: Package, tone: "bg-emerald-50 text-emerald-700", to: "overview" },
    { key: "damaged", label: "Damaged", value: dashboard.damaged, icon: AlertTriangle, tone: "bg-red-50 text-red-700", to: "overview" },
    { key: "under_repair", label: "Under Repair", value: dashboard.under_repair, icon: Wrench, tone: "bg-orange-50 text-orange-700", to: "overview" },
    { key: "missing", label: "Missing", value: dashboard.missing, icon: XCircle, tone: "bg-rose-50 text-rose-700", to: "overview" },
    { key: "disposed", label: "Disposed", value: dashboard.disposed, icon: Trash2, tone: "bg-slate-100 text-slate-600", to: "overview" },
  ];
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
      {cards.map(({ key, label, value, icon: Icon, tone, to }) => (
        <button key={key} onClick={() => onDrill?.(to)} className="text-left"
          data-testid={`dashboard-card-${key}`}>
          <Card className="p-5 border-border bg-white hover:shadow-md transition-shadow h-full">
            <div className="flex items-start justify-between">
              <div className="eyebrow text-muted-foreground">{label}</div>
              <div className={`w-9 h-9 rounded-md flex items-center justify-center ${tone}`}>
                <Icon className="w-4 h-4" />
              </div>
            </div>
            <div className="mt-4 font-display text-3xl font-bold leading-none">
              {(value ?? 0).toLocaleString("en-IN")}
            </div>
          </Card>
        </button>
      ))}
    </div>
  );
}

/* ==================== OVERVIEW (paginated stock) ==================== */
function OverviewPanel({ items, locations, canEdit, canTransfer, onChanged, itemMap, locMap }) {
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [data, setData] = useState({ items: [], total: 0, total_pages: 1 });
  const [filters, setFilters] = useState({ q: "", category: "", condition: "", location_type: "", location_id: "", item_id: "" });
  const [addOpen, setAddOpen] = useState(false);
  const [transferOpen, setTransferOpen] = useState(false);
  const [adjustOpen, setAdjustOpen] = useState(false);
  const [selectedStock, setSelectedStock] = useState(null);

  const load = useCallback(() => {
    const qs = new URLSearchParams({ page, limit,
      ...(filters.q && { q: filters.q }),
      ...(filters.category && { category: filters.category }),
      ...(filters.condition && { condition: filters.condition }),
      ...(filters.location_type && { location_type: filters.location_type }),
      ...(filters.location_id && { location_id: filters.location_id }),
      ...(filters.item_id && { item_id: filters.item_id }),
    });
    api.get(`/inventory/stock/paginated?${qs}`)
      .then((r) => setData(r.data)).catch(() => setData({ items: [], total: 0, total_pages: 1 }));
  }, [page, limit, filters]);

  useEffect(() => { load(); }, [load]);

  const reset = () => { setFilters({ q: "", category: "", condition: "", location_type: "", location_id: "", item_id: "" }); setPage(1); };

  return (
    <Card className="p-6 border-border bg-white">
      <div className="flex items-end justify-between flex-wrap gap-3 mb-4">
        <div>
          <div className="eyebrow text-muted-foreground">Complete Inventory</div>
          <h2 className="font-display text-2xl font-bold mt-1">Overview</h2>
        </div>
        <div className="flex gap-2 flex-wrap">
          {canEdit && (
            <Button onClick={() => setAddOpen(true)} data-testid="btn-add-stock">
              <Plus className="w-4 h-4 mr-1" /> Add Stock
            </Button>
          )}
          {canTransfer && (
            <Button variant="outline" onClick={() => setTransferOpen(true)} data-testid="btn-transfer">
              <ArrowRightLeft className="w-4 h-4 mr-1" /> Transfer
            </Button>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="grid grid-cols-1 md:grid-cols-6 gap-2 mb-4">
        <Input placeholder="Search item / location" value={filters.q}
          onChange={(e) => { setFilters({ ...filters, q: e.target.value }); setPage(1); }}
          data-testid="stock-search" />
        <Select value={filters.category || "all"} onValueChange={(v) => { setFilters({ ...filters, category: v === "all" ? "" : v }); setPage(1); }}>
          <SelectTrigger data-testid="filter-category"><SelectValue placeholder="Category" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            {CATEGORIES.map((c) => <SelectItem key={c.v} value={c.v}>{c.l}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filters.condition || "all"} onValueChange={(v) => { setFilters({ ...filters, condition: v === "all" ? "" : v }); setPage(1); }}>
          <SelectTrigger data-testid="filter-condition"><SelectValue placeholder="Condition" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Conditions</SelectItem>
            {CONDITIONS.map((c) => <SelectItem key={c.v} value={c.v}>{c.l}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filters.location_type || "all"} onValueChange={(v) => { setFilters({ ...filters, location_type: v === "all" ? "" : v }); setPage(1); }}>
          <SelectTrigger data-testid="filter-loc-type"><SelectValue placeholder="Location Type" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Location Types</SelectItem>
            {LOC_TYPES.map((l) => <SelectItem key={l.v} value={l.v}>{l.l}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filters.item_id || "all"} onValueChange={(v) => { setFilters({ ...filters, item_id: v === "all" ? "" : v }); setPage(1); }}>
          <SelectTrigger data-testid="filter-item"><SelectValue placeholder="Item" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Items</SelectItem>
            {items.map((i) => <SelectItem key={i.id} value={i.id}>{i.name}{i.variant ? ` — ${i.variant}` : ""}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button variant="ghost" onClick={reset} data-testid="filter-reset">Reset</Button>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="stock-table">
          <thead>
            <tr className="text-left text-xs uppercase text-muted-foreground border-b">
              <th className="py-2 pr-3">Item</th>
              <th className="py-2 pr-3">Variant</th>
              <th className="py-2 pr-3">Location</th>
              <th className="py-2 pr-3">Type</th>
              <th className="py-2 pr-3">Condition</th>
              <th className="py-2 pr-3 text-right">Qty</th>
              <th className="py-2 pr-3">Notes</th>
              {canEdit && <th className="py-2 pr-3 text-right">Actions</th>}
            </tr>
          </thead>
          <tbody>
            {data.items.map((s) => (
              <tr key={s.id} className="border-b last:border-0 hover:bg-muted/30">
                <td className="py-2 pr-3 font-medium">{s.item?.name || itemMap[s.item_id]?.name || "—"}</td>
                <td className="py-2 pr-3 text-muted-foreground">{s.variant || s.item?.variant || "—"}</td>
                <td className="py-2 pr-3">{s.location?.name || locMap[s.location_id]?.name || "—"}</td>
                <td className="py-2 pr-3 text-xs text-muted-foreground">{locTypeLabel(s.location?.type)}</td>
                <td className="py-2 pr-3">
                  <Badge variant="outline" className={condTone(s.condition)}>{condLabel(s.condition)}</Badge>
                </td>
                <td className="py-2 pr-3 text-right font-mono font-semibold">{s.quantity}</td>
                <td className="py-2 pr-3 text-xs text-muted-foreground truncate max-w-[200px]">{s.notes || ""}</td>
                {canEdit && (
                  <td className="py-2 pr-3 text-right">
                    <Button size="sm" variant="ghost"
                      onClick={() => { setSelectedStock(s); setAdjustOpen(true); }}
                      data-testid={`adjust-${s.id}`}>
                      <SlidersHorizontal className="w-3.5 h-3.5 mr-1" /> Adjust
                    </Button>
                  </td>
                )}
              </tr>
            ))}
            {data.items.length === 0 && (
              <tr><td colSpan={canEdit ? 8 : 7} className="py-8 text-center text-muted-foreground">
                No stock rows found. Add stock to get started.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      <PaginationBar page={page} total={data.total} limit={limit} totalPages={data.total_pages}
        onPageChange={setPage} onLimitChange={(l) => { setLimit(l); setPage(1); }} />

      {addOpen && <AddStockDialog items={items} locations={locations}
        onClose={() => setAddOpen(false)} onDone={() => { setAddOpen(false); load(); onChanged?.(); }} />}
      {transferOpen && <TransferDialog items={items} locations={locations}
        onClose={() => setTransferOpen(false)} onDone={() => { setTransferOpen(false); load(); onChanged?.(); }} />}
      {adjustOpen && selectedStock && <AdjustDialog stock={selectedStock}
        onClose={() => setAdjustOpen(false)} onDone={() => { setAdjustOpen(false); load(); onChanged?.(); }} />}
    </Card>
  );
}

/* ==================== ADD STOCK DIALOG ==================== */
function AddStockDialog({ items, locations, onClose, onDone }) {
  const [form, setForm] = useState({ item_id: "", location_id: "", quantity: "",
    condition: "good", variant: "", notes: "" });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!form.item_id || !form.location_id || !form.quantity) {
      return toast.error("Item, location and quantity are required");
    }
    setSaving(true);
    try {
      await api.post("/inventory/stock", { ...form, quantity: Number(form.quantity),
        variant: form.variant || null, notes: form.notes || null });
      toast.success("Stock added");
      onDone?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg" data-testid="add-stock-dialog">
        <DialogHeader>
          <DialogTitle>Add Stock</DialogTitle>
          <DialogDescription>Add quantity to an item at a location.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <Field label="Item">
            <Select value={form.item_id} onValueChange={(v) => setForm({ ...form, item_id: v })}>
              <SelectTrigger data-testid="add-item"><SelectValue placeholder="Select item" /></SelectTrigger>
              <SelectContent>
                {items.filter((i) => i.active !== false).map((i) => (
                  <SelectItem key={i.id} value={i.id}>{i.name}{i.variant ? ` — ${i.variant}` : ""}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Location">
            <Select value={form.location_id} onValueChange={(v) => setForm({ ...form, location_id: v })}>
              <SelectTrigger data-testid="add-location"><SelectValue placeholder="Select location" /></SelectTrigger>
              <SelectContent>
                {locations.filter((l) => l.active !== false).map((l) => (
                  <SelectItem key={l.id} value={l.id}>{l.name} · {locTypeLabel(l.type)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Quantity">
              <Input type="number" min="1" value={form.quantity} data-testid="add-quantity"
                onChange={(e) => setForm({ ...form, quantity: e.target.value })} />
            </Field>
            <Field label="Condition">
              <Select value={form.condition} onValueChange={(v) => setForm({ ...form, condition: v })}>
                <SelectTrigger data-testid="add-condition"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CONDITIONS.map((c) => <SelectItem key={c.v} value={c.v}>{c.l}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
          </div>
          <Field label="Variant / Spec (optional)">
            <Input value={form.variant} data-testid="add-variant"
              onChange={(e) => setForm({ ...form, variant: e.target.value })} />
          </Field>
          <Field label="Notes (optional)">
            <Textarea rows={2} value={form.notes} data-testid="add-notes"
              onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          </Field>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="add-save">
            {saving ? "Saving…" : "Add Stock"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ==================== TRANSFER DIALOG ==================== */
function TransferDialog({ items, locations, onClose, onDone }) {
  const [form, setForm] = useState({ item_id: "", from_location_id: "", to_location_id: "",
    quantity: "", condition: "good", variant: "", notes: "" });
  const [confirming, setConfirming] = useState(false);
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!form.item_id || !form.from_location_id || !form.to_location_id || !form.quantity) {
      return toast.error("All fields are required");
    }
    if (form.from_location_id === form.to_location_id) {
      return toast.error("From and To must be different");
    }
    setSaving(true);
    try {
      await api.post("/inventory/transfer", { ...form, quantity: Number(form.quantity),
        variant: form.variant || null, notes: form.notes || null });
      toast.success("Transfer complete");
      onDone?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); setConfirming(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg" data-testid="transfer-dialog">
        <DialogHeader>
          <DialogTitle>Transfer Inventory</DialogTitle>
          <DialogDescription>Move stock between locations. Quantities update automatically.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <Field label="Item">
            <Select value={form.item_id} onValueChange={(v) => setForm({ ...form, item_id: v })}>
              <SelectTrigger data-testid="tr-item"><SelectValue placeholder="Select item" /></SelectTrigger>
              <SelectContent>
                {items.map((i) => <SelectItem key={i.id} value={i.id}>{i.name}{i.variant ? ` — ${i.variant}` : ""}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="From">
              <Select value={form.from_location_id} onValueChange={(v) => setForm({ ...form, from_location_id: v })}>
                <SelectTrigger data-testid="tr-from"><SelectValue placeholder="From location" /></SelectTrigger>
                <SelectContent>
                  {locations.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="To">
              <Select value={form.to_location_id} onValueChange={(v) => setForm({ ...form, to_location_id: v })}>
                <SelectTrigger data-testid="tr-to"><SelectValue placeholder="To location" /></SelectTrigger>
                <SelectContent>
                  {locations.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Quantity">
              <Input type="number" min="1" value={form.quantity} data-testid="tr-qty"
                onChange={(e) => setForm({ ...form, quantity: e.target.value })} />
            </Field>
            <Field label="Condition">
              <Select value={form.condition} onValueChange={(v) => setForm({ ...form, condition: v })}>
                <SelectTrigger data-testid="tr-condition"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CONDITIONS.map((c) => <SelectItem key={c.v} value={c.v}>{c.l}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
          </div>
          <Field label="Variant (optional)">
            <Input value={form.variant} onChange={(e) => setForm({ ...form, variant: e.target.value })} />
          </Field>
          <Field label="Notes (optional)">
            <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          </Field>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          {!confirming ? (
            <Button onClick={() => setConfirming(true)} data-testid="tr-review">Review</Button>
          ) : (
            <Button onClick={save} disabled={saving} data-testid="tr-confirm">
              {saving ? "Transferring…" : "Confirm Transfer"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ==================== ADJUST DIALOG ==================== */
function AdjustDialog({ stock, onClose, onDone }) {
  const [form, setForm] = useState({
    new_quantity: String(stock.quantity ?? 0),
    new_condition: stock.condition,
    reason: "",
    notes: "",
  });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!form.reason) return toast.error("Reason is mandatory");
    setSaving(true);
    try {
      await api.post("/inventory/adjust", {
        stock_id: stock.id,
        new_quantity: Number(form.new_quantity),
        new_condition: form.new_condition || null,
        reason: form.reason,
        notes: form.notes || null,
      });
      toast.success("Adjustment saved");
      onDone?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };
  const diff = Number(form.new_quantity) - Number(stock.quantity ?? 0);
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg" data-testid="adjust-dialog">
        <DialogHeader>
          <DialogTitle>Adjust Stock</DialogTitle>
          <DialogDescription>Correct quantity or condition. A reason is mandatory.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <Card className="p-3 bg-muted/40 text-sm">
            <div>Item: <span className="font-medium">{stock.item?.name || stock.item_id}</span></div>
            <div>Location: <span className="font-medium">{stock.location?.name || stock.location_id}</span></div>
            <div>System Qty: <span className="font-mono">{stock.quantity}</span></div>
          </Card>
          <div className="grid grid-cols-2 gap-3">
            <Field label="New Quantity (Physical Count)">
              <Input type="number" min="0" value={form.new_quantity} data-testid="adj-qty"
                onChange={(e) => setForm({ ...form, new_quantity: e.target.value })} />
            </Field>
            <Field label="New Condition">
              <Select value={form.new_condition} onValueChange={(v) => setForm({ ...form, new_condition: v })}>
                <SelectTrigger data-testid="adj-condition"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CONDITIONS.map((c) => <SelectItem key={c.v} value={c.v}>{c.l}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
          </div>
          <div className={`text-sm ${diff < 0 ? "text-destructive" : diff > 0 ? "text-emerald-700" : "text-muted-foreground"}`}>
            Adjustment: {diff > 0 ? `+${diff}` : diff}
          </div>
          <Field label="Reason (mandatory)">
            <Select value={form.reason} onValueChange={(v) => setForm({ ...form, reason: v })}>
              <SelectTrigger data-testid="adj-reason"><SelectValue placeholder="Select reason" /></SelectTrigger>
              <SelectContent>
                {ADJUST_REASONS.map((r) => <SelectItem key={r} value={r}>{r.replace(/_/g, " ")}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Notes">
            <Textarea rows={2} value={form.notes} data-testid="adj-notes"
              onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          </Field>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="adj-save">
            {saving ? "Saving…" : "Save Adjustment"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ==================== ITEM-WISE PANEL ==================== */
function ByItemPanel({ items, locMap }) {
  const [itemId, setItemId] = useState("");
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!itemId) return setData(null);
    api.get(`/inventory/by-item?item_id=${itemId}`).then((r) => setData(r.data)).catch(() => setData(null));
  }, [itemId]);
  const active = items.filter((i) => i.active !== false);
  return (
    <Card className="p-6 border-border bg-white">
      <div className="flex items-end justify-between flex-wrap gap-3 mb-4">
        <div>
          <div className="eyebrow text-muted-foreground">Item-wise</div>
          <h2 className="font-display text-2xl font-bold mt-1">Where is this item?</h2>
        </div>
        <Select value={itemId} onValueChange={setItemId}>
          <SelectTrigger className="w-[280px]" data-testid="by-item-select"><SelectValue placeholder="Select an item" /></SelectTrigger>
          <SelectContent>
            {active.map((i) => <SelectItem key={i.id} value={i.id}>{i.name}{i.variant ? ` — ${i.variant}` : ""}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      {data && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            <Card className="p-3"><div className="eyebrow text-muted-foreground">Total</div><div className="font-mono text-2xl font-bold">{data.total}</div></Card>
            {Object.entries(data.by_condition).map(([c, q]) => (
              <Card key={c} className="p-3"><div className="eyebrow text-muted-foreground">{condLabel(c)}</div><div className="font-mono text-2xl font-bold">{q}</div></Card>
            ))}
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="by-item-table">
              <thead><tr className="text-left text-xs uppercase text-muted-foreground border-b">
                <th className="py-2 pr-3">Location</th><th className="py-2 pr-3">Type</th>
                <th className="py-2 pr-3">Condition</th><th className="py-2 pr-3">Variant</th>
                <th className="py-2 pr-3 text-right">Qty</th><th className="py-2 pr-3">Notes</th>
              </tr></thead>
              <tbody>
                {data.rows.map((r) => (
                  <tr key={r.id} className="border-b last:border-0">
                    <td className="py-2 pr-3">{r.location?.name || locMap[r.location_id]?.name || "—"}</td>
                    <td className="py-2 pr-3 text-xs text-muted-foreground">{locTypeLabel(r.location?.type)}</td>
                    <td className="py-2 pr-3"><Badge variant="outline" className={condTone(r.condition)}>{condLabel(r.condition)}</Badge></td>
                    <td className="py-2 pr-3 text-muted-foreground">{r.variant || "—"}</td>
                    <td className="py-2 pr-3 text-right font-mono font-semibold">{r.quantity}</td>
                    <td className="py-2 pr-3 text-xs text-muted-foreground">{r.notes || ""}</td>
                  </tr>
                ))}
                {data.rows.length === 0 && <tr><td colSpan={6} className="py-8 text-center text-muted-foreground">No stock for this item.</td></tr>}
              </tbody>
            </table>
          </div>
        </>
      )}
      {!itemId && <div className="text-center text-muted-foreground py-8">Select an item to see all its locations.</div>}
    </Card>
  );
}

/* ==================== LOCATION-WISE PANEL ==================== */
function ByLocationPanel({ locations, itemMap }) {
  const [locId, setLocId] = useState("");
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!locId) return setData(null);
    api.get(`/inventory/by-location?location_id=${locId}`).then((r) => setData(r.data)).catch(() => setData(null));
  }, [locId]);
  return (
    <Card className="p-6 border-border bg-white">
      <div className="flex items-end justify-between flex-wrap gap-3 mb-4">
        <div>
          <div className="eyebrow text-muted-foreground">Location-wise</div>
          <h2 className="font-display text-2xl font-bold mt-1">What's in this location?</h2>
        </div>
        <Select value={locId} onValueChange={setLocId}>
          <SelectTrigger className="w-[280px]" data-testid="by-loc-select"><SelectValue placeholder="Select a location" /></SelectTrigger>
          <SelectContent>
            {locations.filter((l) => l.active !== false).map((l) => (
              <SelectItem key={l.id} value={l.id}>{l.name} · {locTypeLabel(l.type)}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      {data && (
        <div className="overflow-x-auto">
          <div className="mb-3 text-sm">Total items in this location: <span className="font-mono font-bold">{data.total}</span></div>
          <table className="w-full text-sm" data-testid="by-loc-table">
            <thead><tr className="text-left text-xs uppercase text-muted-foreground border-b">
              <th className="py-2 pr-3">Item</th><th className="py-2 pr-3">Category</th>
              <th className="py-2 pr-3">Variant</th><th className="py-2 pr-3">Condition</th>
              <th className="py-2 pr-3 text-right">Qty</th><th className="py-2 pr-3">Unit</th>
              <th className="py-2 pr-3">Notes</th>
            </tr></thead>
            <tbody>
              {data.rows.map((r) => (
                <tr key={r.id} className="border-b last:border-0">
                  <td className="py-2 pr-3 font-medium">{r.item?.name || itemMap[r.item_id]?.name || "—"}</td>
                  <td className="py-2 pr-3 text-muted-foreground">{catLabel(r.item?.category)}</td>
                  <td className="py-2 pr-3 text-muted-foreground">{r.variant || r.item?.variant || "—"}</td>
                  <td className="py-2 pr-3"><Badge variant="outline" className={condTone(r.condition)}>{condLabel(r.condition)}</Badge></td>
                  <td className="py-2 pr-3 text-right font-mono font-semibold">{r.quantity}</td>
                  <td className="py-2 pr-3 text-muted-foreground">{r.item?.unit || "—"}</td>
                  <td className="py-2 pr-3 text-xs text-muted-foreground">{r.notes || ""}</td>
                </tr>
              ))}
              {data.rows.length === 0 && <tr><td colSpan={7} className="py-8 text-center text-muted-foreground">No stock in this location.</td></tr>}
            </tbody>
          </table>
        </div>
      )}
      {!locId && <div className="text-center text-muted-foreground py-8">Select a location to see its inventory.</div>}
    </Card>
  );
}

/* ==================== ROOM MATRIX PANEL ==================== */
function MatrixPanel() {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  useEffect(() => { api.get("/inventory/room-matrix").then((r) => setData(r.data)).catch(() => {}); }, []);
  if (!data) return <Card className="p-8 text-muted-foreground">Loading…</Card>;
  const items = data.items.filter((i) => {
    if (!q) return true;
    return i.name.toLowerCase().includes(q.toLowerCase());
  });
  const locations = data.locations.filter((l) => l.type === "hostel_room");
  const filteredLocs = locations.filter((l) => {
    if (!q) return true;
    return l.name.toLowerCase().includes(q.toLowerCase());
  });
  return (
    <Card className="p-6 border-border bg-white">
      <div className="flex items-end justify-between flex-wrap gap-3 mb-4">
        <div>
          <div className="eyebrow text-muted-foreground">Room Matrix</div>
          <h2 className="font-display text-2xl font-bold mt-1">Rooms × Items</h2>
          <p className="text-sm text-muted-foreground mt-1">{filteredLocs.length} rooms × {items.length} items</p>
        </div>
        <Input placeholder="Filter room or item…" value={q} onChange={(e) => setQ(e.target.value)}
          className="w-[260px]" data-testid="matrix-search" />
      </div>
      <div className="overflow-auto max-h-[600px] border rounded-md">
        <table className="w-full text-xs" data-testid="matrix-table">
          <thead className="sticky top-0 bg-white z-10">
            <tr className="border-b">
              <th className="py-2 pr-3 pl-2 text-left sticky left-0 bg-white">Room</th>
              {items.map((i) => (
                <th key={i.id} className="py-2 pr-2 text-center min-w-[80px]" title={i.name}>{i.name}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filteredLocs.map((l) => (
              <tr key={l.id} className="border-b hover:bg-muted/30">
                <td className="py-2 pr-3 pl-2 font-medium sticky left-0 bg-white">{l.name}</td>
                {items.map((i) => {
                  const qty = data.matrix[l.id]?.[i.id] || 0;
                  return (
                    <td key={i.id} className="py-2 pr-2 text-center font-mono">
                      {qty > 0 ? qty : <span className="text-muted-foreground">—</span>}
                    </td>
                  );
                })}
              </tr>
            ))}
            {filteredLocs.length === 0 && <tr><td colSpan={items.length + 1} className="py-8 text-center text-muted-foreground">No matching rooms.</td></tr>}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

/* ==================== STORE PANEL ==================== */
function StorePanel({ locations, itemMap }) {
  const storeLocs = locations.filter((l) => l.type === "store");
  const [locId, setLocId] = useState(storeLocs[0]?.id || "");
  useEffect(() => {
    if (!locId && storeLocs.length) setLocId(storeLocs[0].id);
  }, [locId, storeLocs]);
  return (
    <div className="space-y-4">
      {storeLocs.length === 0 ? (
        <Card className="p-8 text-center">
          <Warehouse className="w-8 h-8 mx-auto text-muted-foreground" />
          <div className="mt-3 font-medium">No Store location configured yet</div>
          <p className="text-sm text-muted-foreground mt-1">
            Create one under the <b>Locations</b> tab with type = Store.
          </p>
        </Card>
      ) : (
        <>
          {storeLocs.length > 1 && (
            <div className="flex gap-2 items-center">
              <Label className="text-sm">Store:</Label>
              <Select value={locId} onValueChange={setLocId}>
                <SelectTrigger className="w-[280px]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {storeLocs.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <ByLocationPanelBody locId={locId} itemMap={itemMap} />
        </>
      )}
    </div>
  );
}

function ByLocationPanelBody({ locId, itemMap }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!locId) return;
    api.get(`/inventory/by-location?location_id=${locId}`).then((r) => setData(r.data)).catch(() => {});
  }, [locId]);
  if (!data) return <Card className="p-8 text-muted-foreground">Loading…</Card>;
  return (
    <Card className="p-6 border-border bg-white">
      <div className="mb-3 text-sm">Total items: <span className="font-mono font-bold">{data.total}</span></div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="store-table">
          <thead><tr className="text-left text-xs uppercase text-muted-foreground border-b">
            <th className="py-2 pr-3">Item</th><th className="py-2 pr-3">Variant</th>
            <th className="py-2 pr-3">Condition</th><th className="py-2 pr-3 text-right">Qty</th>
            <th className="py-2 pr-3">Notes</th>
          </tr></thead>
          <tbody>
            {data.rows.map((r) => (
              <tr key={r.id} className="border-b last:border-0">
                <td className="py-2 pr-3 font-medium">{r.item?.name || itemMap[r.item_id]?.name || "—"}</td>
                <td className="py-2 pr-3 text-muted-foreground">{r.variant || r.item?.variant || "—"}</td>
                <td className="py-2 pr-3"><Badge variant="outline" className={condTone(r.condition)}>{condLabel(r.condition)}</Badge></td>
                <td className="py-2 pr-3 text-right font-mono font-semibold">{r.quantity}</td>
                <td className="py-2 pr-3 text-xs text-muted-foreground">{r.notes || ""}</td>
              </tr>
            ))}
            {data.rows.length === 0 && <tr><td colSpan={5} className="py-8 text-center text-muted-foreground">Store is empty.</td></tr>}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

/* ==================== MOVEMENTS PANEL ==================== */
function MovementsPanel() {
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [action, setAction] = useState("");
  const [data, setData] = useState({ items: [], total: 0, total_pages: 1 });
  const load = useCallback(() => {
    const qs = new URLSearchParams({ page, limit, ...(action && { action }) });
    api.get(`/inventory/movements?${qs}`).then((r) => setData(r.data)).catch(() => {});
  }, [page, limit, action]);
  useEffect(() => { load(); }, [load]);
  return (
    <Card className="p-6 border-border bg-white">
      <div className="flex items-end justify-between flex-wrap gap-3 mb-4">
        <div>
          <div className="eyebrow text-muted-foreground">Audit Trail</div>
          <h2 className="font-display text-2xl font-bold mt-1">Movement History</h2>
        </div>
        <Select value={action || "all"} onValueChange={(v) => { setAction(v === "all" ? "" : v); setPage(1); }}>
          <SelectTrigger className="w-[200px]" data-testid="mv-action"><SelectValue placeholder="All actions" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All actions</SelectItem>
            <SelectItem value="add">Add</SelectItem>
            <SelectItem value="transfer">Transfer</SelectItem>
            <SelectItem value="adjust">Adjust</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="mv-table">
          <thead><tr className="text-left text-xs uppercase text-muted-foreground border-b">
            <th className="py-2 pr-3">Date</th><th className="py-2 pr-3">Action</th>
            <th className="py-2 pr-3">Item</th><th className="py-2 pr-3">From</th>
            <th className="py-2 pr-3">To</th><th className="py-2 pr-3 text-right">Qty Δ</th>
            <th className="py-2 pr-3">Reason</th><th className="py-2 pr-3">By</th>
          </tr></thead>
          <tbody>
            {data.items.map((m) => (
              <tr key={m.id} className="border-b last:border-0">
                <td className="py-2 pr-3 text-xs font-mono text-muted-foreground">{(m.at || "").slice(0, 19).replace("T", " ")}</td>
                <td className="py-2 pr-3"><Badge variant="outline" className="capitalize">{m.action}</Badge></td>
                <td className="py-2 pr-3">{m.item?.name || "—"}</td>
                <td className="py-2 pr-3 text-muted-foreground">{m.from_location?.name || "—"}</td>
                <td className="py-2 pr-3">{m.to_location?.name || "—"}</td>
                <td className={`py-2 pr-3 text-right font-mono ${m.quantity < 0 ? "text-destructive" : m.quantity > 0 ? "text-emerald-700" : ""}`}>
                  {m.quantity > 0 ? `+${m.quantity}` : m.quantity}
                </td>
                <td className="py-2 pr-3 text-xs text-muted-foreground">{m.reason || m.notes || ""}</td>
                <td className="py-2 pr-3 text-xs">{m.user_name} · <span className="text-muted-foreground">{m.user_role}</span></td>
              </tr>
            ))}
            {data.items.length === 0 && <tr><td colSpan={8} className="py-8 text-center text-muted-foreground">No movements yet.</td></tr>}
          </tbody>
        </table>
      </div>
      <PaginationBar page={page} total={data.total} limit={limit} totalPages={data.total_pages}
        onPageChange={setPage} onLimitChange={(l) => { setLimit(l); setPage(1); }} />
    </Card>
  );
}

/* ==================== ITEMS PANEL ==================== */
function ItemsPanel({ items, canEdit, canDelete, onChanged }) {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [q, setQ] = useState("");
  const filtered = items.filter((i) => !q || i.name.toLowerCase().includes(q.toLowerCase()));
  const del = async (i) => {
    if (!window.confirm(`Delete item "${i.name}"? Items with live stock will be marked inactive instead.`)) return;
    try {
      const r = await api.delete(`/inventory/items/${i.id}`);
      toast.success(r.data?.message || "Deleted");
      onChanged?.();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <Card className="p-6 border-border bg-white">
      <div className="flex items-end justify-between flex-wrap gap-3 mb-4">
        <div>
          <div className="eyebrow text-muted-foreground">Item Master</div>
          <h2 className="font-display text-2xl font-bold mt-1">Items</h2>
        </div>
        <div className="flex gap-2 items-center flex-wrap">
          <Input placeholder="Search item…" value={q} onChange={(e) => setQ(e.target.value)}
            className="w-[240px]" data-testid="items-search" />
          {canEdit && <Button onClick={() => { setEditing(null); setOpen(true); }} data-testid="btn-add-item">
            <Plus className="w-4 h-4 mr-1" /> Add Item
          </Button>}
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="items-table">
          <thead><tr className="text-left text-xs uppercase text-muted-foreground border-b">
            <th className="py-2 pr-3">Name</th><th className="py-2 pr-3">Category</th>
            <th className="py-2 pr-3">Unit</th>
            <th className="py-2 pr-3 text-right">Current Stock</th>
            <th className="py-2 pr-3">Variant</th>
            <th className="py-2 pr-3">Status</th>
            {canEdit && <th className="py-2 pr-3 text-right">Actions</th>}
          </tr></thead>
          <tbody>
            {filtered.map((i) => (
              <tr key={i.id} className="border-b last:border-0">
                <td className="py-2 pr-3 font-medium">{i.name}</td>
                <td className="py-2 pr-3 text-muted-foreground">{catLabel(i.category)}</td>
                <td className="py-2 pr-3 text-muted-foreground capitalize">{i.unit}</td>
                <td className="py-2 pr-3 text-right font-mono font-semibold" data-testid={`item-qty-${i.id}`}>
                  <span className={(i.current_quantity ?? 0) === 0 ? "text-muted-foreground" : ""}>
                    {(i.current_quantity ?? 0).toLocaleString("en-IN")}
                  </span>
                  <span className="ml-1 text-xs text-muted-foreground">{i.unit || "unit"}{(i.current_quantity ?? 0) === 1 ? "" : "s"}</span>
                </td>
                <td className="py-2 pr-3 text-muted-foreground">{i.variant || "—"}</td>
                <td className="py-2 pr-3">{i.active === false ? <Badge variant="secondary">Inactive</Badge> : <Badge>Active</Badge>}</td>
                {canEdit && (
                  <td className="py-2 pr-3 text-right">
                    <Button size="sm" variant="ghost" onClick={() => { setEditing(i); setOpen(true); }}
                      data-testid={`edit-item-${i.id}`}><Pencil className="w-3.5 h-3.5" /></Button>
                    {canDelete && (
                      <Button size="sm" variant="ghost" onClick={() => del(i)}
                        data-testid={`del-item-${i.id}`}><Trash2 className="w-3.5 h-3.5 text-destructive" /></Button>
                    )}
                  </td>
                )}
              </tr>
            ))}
            {filtered.length === 0 && <tr><td colSpan={canEdit ? 7 : 6} className="py-8 text-center text-muted-foreground">No items yet.</td></tr>}
          </tbody>
        </table>
      </div>
      {open && <ItemDialog editing={editing} onClose={() => setOpen(false)}
        onDone={() => { setOpen(false); onChanged?.(); }} />}
    </Card>
  );
}

function ItemDialog({ editing, onClose, onDone }) {
  const [form, setForm] = useState(editing || { name: "", category: "other", unit: "piece",
    variant: "", description: "", notes: "", bundle_size: "", active: true });
  // v34 — Current physical quantity: number user can set directly on the item.
  const [qty, setQty] = useState(editing?.current_quantity ?? "");
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!form.name?.trim()) return toast.error("Name is required");
    const qNum = qty === "" || qty === null ? null : Number(qty);
    if (qNum !== null && (!Number.isFinite(qNum) || qNum < 0)) {
      return toast.error("Current Quantity must be a non-negative number");
    }
    setSaving(true);
    try {
      const payload = { ...form,
        bundle_size: form.bundle_size ? Number(form.bundle_size) : null,
        variant: form.variant || null,
        description: form.description || null,
        notes: form.notes || null,
        initial_quantity: qNum };
      if (editing) await api.put(`/inventory/items/${editing.id}`, payload);
      else await api.post("/inventory/items", payload);
      toast.success(editing ? "Item updated" : "Item created");
      onDone?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg" data-testid="item-dialog">
        <DialogHeader><DialogTitle>{editing ? "Edit Item" : "New Item"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Field label="Name"><Input value={form.name} data-testid="item-name"
            onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Category">
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger data-testid="item-category"><SelectValue /></SelectTrigger>
                <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c.v} value={c.v}>{c.l}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Unit">
              <Select value={form.unit} onValueChange={(v) => setForm({ ...form, unit: v })}>
                <SelectTrigger data-testid="item-unit"><SelectValue /></SelectTrigger>
                <SelectContent>{UNITS.map((u) => <SelectItem key={u.v} value={u.v}>{u.l}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
          </div>
          <Field label="Variant / Specification">
            <Input value={form.variant || ""} data-testid="item-variant"
              onChange={(e) => setForm({ ...form, variant: e.target.value })} />
          </Field>
          <Field label={editing ? "Current Quantity (override)" : "Initial Quantity"}>
            <div className="flex items-center gap-2">
              <Input type="number" min="0" step="1" value={qty}
                onChange={(e) => setQty(e.target.value)}
                placeholder="e.g. 25"
                data-testid="item-current-quantity" />
              <span className="text-xs text-muted-foreground shrink-0">
                {form.unit || "unit"}{qty && Number(qty) === 1 ? "" : "s"}
              </span>
            </div>
            <div className="text-xs text-muted-foreground mt-1">
              {editing
                ? "Overwrites the current total (any delta is logged as a stock movement)."
                : "Optional. Adds this many units to the auto-created Main Store."}
            </div>
          </Field>
          <Field label="Bundle Size (for bundles/packs)">
            <Input type="number" value={form.bundle_size || ""} data-testid="item-bundle"
              onChange={(e) => setForm({ ...form, bundle_size: e.target.value })} />
          </Field>
          <Field label="Description">
            <Textarea rows={2} value={form.description || ""}
              onChange={(e) => setForm({ ...form, description: e.target.value })} />
          </Field>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="item-save">
            {saving ? "Saving…" : (editing ? "Update" : "Create")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ==================== LOCATIONS PANEL ==================== */
function LocationsPanel({ locations, canEdit, canDelete, onChanged }) {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [q, setQ] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const filtered = locations.filter((l) => {
    if (typeFilter && l.type !== typeFilter) return false;
    if (q && !l.name.toLowerCase().includes(q.toLowerCase())) return false;
    return true;
  });
  const del = async (l) => {
    if (l.auto_created) return toast.error("Auto-mirrored room locations cannot be deleted here.");
    if (!window.confirm(`Delete location "${l.name}"?`)) return;
    try {
      const r = await api.delete(`/inventory/locations/${l.id}`);
      toast.success(r.data?.message || "Deleted");
      onChanged?.();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <Card className="p-6 border-border bg-white">
      <div className="flex items-end justify-between flex-wrap gap-3 mb-4">
        <div>
          <div className="eyebrow text-muted-foreground">Locations</div>
          <h2 className="font-display text-2xl font-bold mt-1">Locations</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Existing hostel rooms are auto-mirrored. You can add custom locations too.
          </p>
        </div>
        <div className="flex gap-2 items-center flex-wrap">
          <Input placeholder="Search…" value={q} onChange={(e) => setQ(e.target.value)}
            className="w-[200px]" data-testid="loc-search" />
          <Select value={typeFilter || "all"} onValueChange={(v) => setTypeFilter(v === "all" ? "" : v)}>
            <SelectTrigger className="w-[180px]" data-testid="loc-type-filter"><SelectValue placeholder="All types" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All types</SelectItem>
              {LOC_TYPES.map((l) => <SelectItem key={l.v} value={l.v}>{l.l}</SelectItem>)}
            </SelectContent>
          </Select>
          {canEdit && <Button onClick={() => { setEditing(null); setOpen(true); }} data-testid="btn-add-location">
            <Plus className="w-4 h-4 mr-1" /> Add Location
          </Button>}
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="loc-table">
          <thead><tr className="text-left text-xs uppercase text-muted-foreground border-b">
            <th className="py-2 pr-3">Name</th><th className="py-2 pr-3">Type</th>
            <th className="py-2 pr-3">Source</th><th className="py-2 pr-3">Status</th>
            {canEdit && <th className="py-2 pr-3 text-right">Actions</th>}
          </tr></thead>
          <tbody>
            {filtered.map((l) => (
              <tr key={l.id} className="border-b last:border-0">
                <td className="py-2 pr-3 font-medium">{l.name}</td>
                <td className="py-2 pr-3 text-muted-foreground">{locTypeLabel(l.type)}</td>
                <td className="py-2 pr-3 text-xs text-muted-foreground">
                  {l.auto_created ? <Badge variant="outline">Auto (Room)</Badge> : <Badge variant="secondary">Custom</Badge>}
                </td>
                <td className="py-2 pr-3">{l.active === false ? <Badge variant="secondary">Inactive</Badge> : <Badge>Active</Badge>}</td>
                {canEdit && (
                  <td className="py-2 pr-3 text-right">
                    <Button size="sm" variant="ghost" onClick={() => { setEditing(l); setOpen(true); }}
                      data-testid={`edit-loc-${l.id}`}><Pencil className="w-3.5 h-3.5" /></Button>
                    {canDelete && !l.auto_created && (
                      <Button size="sm" variant="ghost" onClick={() => del(l)}
                        data-testid={`del-loc-${l.id}`}><Trash2 className="w-3.5 h-3.5 text-destructive" /></Button>
                    )}
                  </td>
                )}
              </tr>
            ))}
            {filtered.length === 0 && <tr><td colSpan={canEdit ? 5 : 4} className="py-8 text-center text-muted-foreground">No locations.</td></tr>}
          </tbody>
        </table>
      </div>
      {open && <LocationDialog editing={editing} onClose={() => setOpen(false)}
        onDone={() => { setOpen(false); onChanged?.(); }} />}
    </Card>
  );
}

function LocationDialog({ editing, onClose, onDone }) {
  const [form, setForm] = useState(editing || { name: "", type: "store",
    room_id: null, description: "", active: true });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!form.name?.trim()) return toast.error("Name is required");
    setSaving(true);
    try {
      const payload = { ...form, description: form.description || null,
        room_id: form.room_id || null };
      if (editing) await api.put(`/inventory/locations/${editing.id}`, payload);
      else await api.post("/inventory/locations", payload);
      toast.success(editing ? "Location updated" : "Location created");
      onDone?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg" data-testid="loc-dialog">
        <DialogHeader><DialogTitle>{editing ? "Edit Location" : "New Location"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Field label="Name"><Input value={form.name} data-testid="loc-name"
            onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field>
          <Field label="Type">
            <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
              <SelectTrigger data-testid="loc-type"><SelectValue /></SelectTrigger>
              <SelectContent>{LOC_TYPES.filter((t) => t.v !== "hostel_room").map((t) => (
                <SelectItem key={t.v} value={t.v}>{t.l}</SelectItem>
              ))}</SelectContent>
            </Select>
          </Field>
          <Field label="Description">
            <Textarea rows={2} value={form.description || ""}
              onChange={(e) => setForm({ ...form, description: e.target.value })} />
          </Field>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="loc-save">
            {saving ? "Saving…" : (editing ? "Update" : "Create")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ==================== IMPORT PANEL ==================== */
function ImportPanel({ onDone }) {
  const [rows, setRows] = useState([]);
  const [preview, setPreview] = useState(null);
  const [running, setRunning] = useState(false);

  const parseFile = async (file) => {
    if (!file) return;
    const text = await file.text();
    const lines = text.split(/\r?\n/).filter((l) => l.trim());
    if (lines.length < 2) return toast.error("File must have a header row and at least one data row");
    const header = lines[0].split(",").map((h) => h.trim().toLowerCase().replace(/[^a-z]/g, "_"));
    // Expected: item_name, location_name, quantity, variant, condition, category, unit, notes
    const need = ["item_name", "location_name", "quantity"];
    for (const h of need) if (!header.includes(h)) return toast.error(`Missing column: ${h}`);
    const idx = (k) => header.indexOf(k);
    const parsed = lines.slice(1).map((ln) => {
      const cols = ln.split(",").map((c) => c.trim());
      return {
        item_name: cols[idx("item_name")],
        location_name: cols[idx("location_name")],
        quantity: Number(cols[idx("quantity")] || 0),
        variant: idx("variant") >= 0 ? cols[idx("variant")] || null : null,
        condition: idx("condition") >= 0 ? cols[idx("condition")] || "good" : "good",
        category: idx("category") >= 0 ? cols[idx("category")] || null : null,
        unit: idx("unit") >= 0 ? cols[idx("unit")] || null : null,
        notes: idx("notes") >= 0 ? cols[idx("notes")] || null : null,
      };
    });
    setRows(parsed); setPreview(null);
    // Get preview from server
    try {
      const r = await api.post("/inventory/import", { rows: parsed, confirm: false });
      setPreview(r.data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const confirm = async () => {
    setRunning(true);
    try {
      const r = await api.post("/inventory/import", { rows, confirm: true });
      toast.success(`Imported: ${r.data.rows_processed} rows, ${r.data.items_created} new items`);
      setRows([]); setPreview(null);
      onDone?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setRunning(false); }
  };

  return (
    <Card className="p-6 border-border bg-white">
      <div className="mb-4">
        <div className="eyebrow text-muted-foreground">Bulk Load</div>
        <h2 className="font-display text-2xl font-bold mt-1">Import Inventory</h2>
        <p className="text-sm text-muted-foreground mt-1">
          Upload a CSV with columns: <code>item_name, location_name, quantity, variant, condition, category, unit, notes</code>.
          Location names must already exist. Unknown items will be created.
        </p>
      </div>
      <div className="flex items-center gap-3">
        <Input type="file" accept=".csv,text/csv" data-testid="import-file"
          onChange={(e) => parseFile(e.target.files?.[0])} />
      </div>
      {preview && (
        <div className="mt-4">
          <div className="mb-2 text-sm">
            {preview.total} rows · <span className={preview.with_errors ? "text-destructive font-medium" : "text-emerald-700"}>
              {preview.with_errors} errors
            </span>
          </div>
          <div className="max-h-[400px] overflow-auto border rounded-md">
            <table className="w-full text-xs" data-testid="import-preview-table">
              <thead className="sticky top-0 bg-white"><tr className="border-b">
                <th className="py-1 px-2 text-left">#</th>
                <th className="py-1 px-2 text-left">Item</th>
                <th className="py-1 px-2 text-left">Location</th>
                <th className="py-1 px-2 text-right">Qty</th>
                <th className="py-1 px-2 text-left">Item Action</th>
                <th className="py-1 px-2 text-left">Errors</th>
              </tr></thead>
              <tbody>
                {preview.rows.map((r) => (
                  <tr key={r.row} className={`border-b ${r.errors.length ? "bg-red-50" : ""}`}>
                    <td className="py-1 px-2">{r.row}</td>
                    <td className="py-1 px-2">{r.item_name}{r.variant ? ` — ${r.variant}` : ""}</td>
                    <td className="py-1 px-2">{r.location_name}</td>
                    <td className="py-1 px-2 text-right font-mono">{r.quantity}</td>
                    <td className="py-1 px-2">{r.item_action}</td>
                    <td className="py-1 px-2 text-red-700">{r.errors.join(", ")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="mt-3 flex gap-2 justify-end">
            <Button variant="outline" onClick={() => { setRows([]); setPreview(null); }}>Cancel</Button>
            <Button onClick={confirm} disabled={running || preview.with_errors > 0} data-testid="import-confirm">
              {running ? "Importing…" : `Confirm Import (${preview.total})`}
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
}

/* ==================== REPORTS PANEL ==================== */
function ReportsPanel() {
  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const download = async (kind) => {
    try {
      const token = localStorage.getItem("hostel_token");
      const url = `${API_URL}/api/inventory/export/xlsx?kind=${kind}`;
      const resp = await fetch(url, { credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : {} });
      if (!resp.ok) throw new Error("Download failed");
      const blob = await resp.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `inventory-${kind}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      toast.success("Downloaded");
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const reports = [
    { key: "stock", label: "Complete Inventory", desc: "All items × locations × conditions" },
    { key: "movements", label: "Movement History", desc: "All transfers, additions and adjustments" },
    { key: "damaged", label: "Damaged / Missing / Repair / Disposed", desc: "Only non-good stock" },
  ];
  return (
    <Card className="p-6 border-border bg-white">
      <div className="mb-4">
        <div className="eyebrow text-muted-foreground">Reports</div>
        <h2 className="font-display text-2xl font-bold mt-1">Download Inventory Reports</h2>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {reports.map((r) => (
          <Card key={r.key} className="p-4">
            <div className="font-medium">{r.label}</div>
            <div className="text-xs text-muted-foreground mt-1 mb-3">{r.desc}</div>
            <Button size="sm" onClick={() => download(r.key)} data-testid={`report-${r.key}`}>
              <Download className="w-3.5 h-3.5 mr-1" /> Download XLSX
            </Button>
          </Card>
        ))}
      </div>
    </Card>
  );
}

/* ==================== Utility ==================== */
function Field({ label, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs uppercase tracking-wider text-muted-foreground">{label}</Label>
      {children}
    </div>
  );
}
